import React, { useState } from 'react';
import { CustomCellProps } from 'components/common/grid/';
import { Box, Typography } from '@mui/material';
import { AsyncActionData, gridAPI } from 'api/grid';
import { getCellContent, getCellWithTooltip } from './index';
import { APIResponse } from 'tools/types';
import { openInNewTab } from 'tools/tools';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import useBundleTranslation from 'i18n';
import { downloadAPI } from 'api/api';
import { useMiNavigation } from 'hooks/useNavigation';
import pollingService from 'tools/pollingService';

export default function AsyncActionCell({
    text,
    tooltip,
    subText,
    icon,
    href,
    reloadGridData,
    setShowLoader,
    rawHtml,
    align,
    rawTooltipHtml,
    t,
    setErrorMessage,
    confirmation,
    download,
}: CustomCellProps) {
    const navigate = useMiNavigation();
    const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
    if (undefined === t) t = useBundleTranslation('main')['t'];

    const { text: confirmationText, ...confirmationPopupSettings } = confirmation ?? {};

    const onClick = () => {
        if (confirmationText) {
            setOpenConfirmation(true);
            return;
        }
        doAction();
    };

    const doAction = () => {
        if (href && reloadGridData && setShowLoader) {
            if (setErrorMessage) {
                setErrorMessage('');
            }

            if (download === true) {
                downloadAPI.export.getDownload(href);

                return;
            }

            let keepLoading = false;
            setShowLoader(true);
            gridAPI
                .asyncAction(href)
                .then((response: APIResponse<AsyncActionData> | { status: 'QUEUE'; data: { ppl_id: number } }) => {
                    if (response.status === 'QUEUE') {
                        keepLoading = true;
                        const { run } = pollingService({
                            onSuccess: () => {
                                reloadGridData();
                                setShowLoader(false);
                            },
                            onError: () => {
                                setShowLoader(false);
                            },
                            requestData: {},
                        });
                        run(response.data.ppl_id);
                        return;
                    }
                    if (response.status === 'OK') {
                        const responseData = (response as APIResponse<AsyncActionData>).data;

                        if (responseData.reloadGridData) {
                            reloadGridData();
                        }

                        if (responseData.showConfirmation) {
                            alert(responseData.message);
                        }

                        if (responseData.href) {
                            if (responseData.newTab) {
                                openInNewTab(responseData.href);
                            } else {
                                navigate(responseData.href);
                            }
                        }
                    }
                    if (response.status === 'ERROR') {
                        if (setErrorMessage) {
                            const defaultMessage = t ? t('error') : 'Error';
                            setErrorMessage((response as APIResponse<AsyncActionData>).message ?? defaultMessage);
                        }
                    }
                })
                .finally(() => {
                    if (!keepLoading) {
                        setShowLoader(false);
                    }
                });
        }
    };

    let mainText = (
        <Box component={'a'} onClick={onClick} sx={{ cursor: 'pointer' }}>
            {getCellContent(text, icon, rawHtml, align, subText)}
        </Box>
    );

    if (tooltip) {
        mainText = getCellWithTooltip(mainText, tooltip, rawTooltipHtml, t);
    }
    const popupSettings: PopupSettings = {
        title: confirmationPopupSettings?.title ?? 'confirmation',
        textOK: confirmationPopupSettings?.textOK ?? 'ok',
    };
    return (
        <>
            {mainText}
            {openConfirmation && (
                <Popup
                    settings={popupSettings}
                    open={openConfirmation}
                    t={t}
                    onHide={() => setOpenConfirmation(false)}
                    onConfirm={() => {
                        setOpenConfirmation(false);
                        doAction();
                    }}
                    maxWidth={'popupSm'}
                >
                    <Typography>{t(confirmationText ?? '', { name: 'itemName' })}</Typography>
                </Popup>
            )}
        </>
    );
}
